import { tt } from '@agos/shared'

/**
 * The kitchen's *zvonček* - FRONTEND.md §9.720 and §9.721, owner O95, on `BACKEND.md` §10A.369.
 *
 * <p><b>The owner</b>: *"in Kuchyňa add actually zvonček button, where he can bell čašníčky in nová objednávka, in
 * mapovanie stolov, otvorené stoly etc... If you have polievka, this go first, so you will not mark it as hotová"*.
 * §9.675's bell rings on a status; a soup that goes out before the main course changes none.
 *
 * <p>The rules a screen needs, with no network and no React in them: what a call says, which ticket offers the bell,
 * and the high-water mark that decides what rings. The listener (`use-kitchen-calls.tsx`) and the button
 * (`KitchenCallButton.tsx`) are the two places they are used.
 */

/** How often a waiter's screen asks - §9.720's five seconds. */
export const KITCHEN_CALLS_POLL_MS = 5_000

/** How long a bell the kitchen pressed stays held, so a double tap rings once. */
export const KITCHEN_CALL_HOLD_MS = 5_000

/**
 * The most calls one read answers - the server's `KitchenCallService.LIMIT`. A full answer may have more behind it,
 * so the mark then moves to the last call handed over rather than to the newest id.
 */
export const KITCHEN_CALLS_LIMIT = 20

/** How long the call's toast stays up. A waiter fifty metres from the kitchen needs longer than the default. */
export const KITCHEN_CALL_TOAST_MS = 12_000

/** One call as `GET /kitchen/calls` answers it. */
export interface KitchenCall {
  id: number
  /** Null for the general bell. */
  orderId: number | null
  orderNumber: string | null
  /** The table's own name, without *Stôl*. Null for the general bell and an order with no table. */
  tableName: string | null
  createdAt: string | null
  createdByName: string | null
}

/** The read: the newest call's id when it ran, and the calls after the screen's mark. */
export interface KitchenCallsAnswer {
  latestId: number
  calls: KitchenCall[]
}

const isWholeNumber = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value)

const textOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

/** One call off the wire, or null when it carries no id - a row the mark cannot be moved past is not a call. */
export function kitchenCallOf(payload: unknown): KitchenCall | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (!isWholeNumber(row.id)) return null
  return {
    id: row.id,
    orderId: isWholeNumber(row.orderId) ? row.orderId : null,
    orderNumber: textOrNull(row.orderNumber),
    tableName: textOrNull(row.tableName),
    createdAt: textOrNull(row.createdAt),
    createdByName: textOrNull(row.createdByName),
  }
}

/**
 * The read off the wire, or null when it cannot be read.
 *
 * <p><b>Null rather than an empty answer</b>, because the step treats null as a failed read - it rings nothing and
 * keeps its mark. An answer with no `latestId` turned into `{latestId: 0}` would move nothing today and would reset a
 * screen's mark the day the rule changes.
 */
export function kitchenCallsOf(payload: unknown): KitchenCallsAnswer | null {
  if (typeof payload !== 'object' || payload === null) return null
  const body = payload as Record<string, unknown>
  if (!isWholeNumber(body.latestId) || body.latestId < 0 || !Array.isArray(body.calls)) return null
  return {
    latestId: body.latestId,
    calls: body.calls.flatMap((entry) => {
      const call = kitchenCallOf(entry)
      return call === null ? [] : [call]
    }),
  }
}

/** The read's parameters from this mark - none before the first answer, so the server answers only its id. */
export function kitchenCallsParams(mark: number | null): { afterId: number } | undefined {
  return mark === null ? undefined : { afterId: mark }
}

/** What one answer does to a screen: its next mark, whether it rings, and the calls it shows. */
export interface KitchenCallStep {
  mark: number | null
  ring: boolean
  announce: KitchenCall[]
}

/**
 * The high-water mark - §9.720.
 *
 * <ul>
 *   <li><b>A failed or unreadable read</b> (`answer` null) rings nothing and keeps the mark: the next answer catches
 *       up, inside the server's two minutes.
 *   <li><b>The first answer only sets the mark</b>, so a screen opened after a call does not ring for it - §9.675's
 *       *first seen already ready does not ring*.
 *   <li><b>Later calls ring once and are each shown</b>, however many one poll brings. A call at or below the mark is
 *       left out, so the same answer read twice (a remount, StrictMode) cannot ring twice.
 *   <li><b>The next mark</b> is the larger of the newest id and the last call handed over - unless the answer was
 *       full, when there may be more behind it and the mark stops at the last call shown.
 * </ul>
 */
export function kitchenCallStep(mark: number | null, answer: KitchenCallsAnswer | null): KitchenCallStep {
  if (answer === null) return { mark, ring: false, announce: [] }
  if (mark === null) return { mark: answer.latestId, ring: false, announce: [] }
  const fresh = answer.calls.filter((call) => call.id > mark).sort((left, right) => left.id - right.id)
  const lastShown = fresh.length > 0 ? (fresh[fresh.length - 1]?.id ?? mark) : mark
  const full = answer.calls.length >= KITCHEN_CALLS_LIMIT
  const next = full ? Math.max(mark, lastShown) : Math.max(mark, answer.latestId, lastShown)
  return { mark: next, ring: fresh.length > 0, announce: fresh }
}

/**
 * What a waiter reads - *Kuchyňa volá: Stôl 5 (#1234)*, *Kuchyňa volá: #1234* for an order with no table, *Kuchyňa
 * volá* for the general bell.
 */
export function kitchenCallMessage(call: Pick<KitchenCall, 'orderNumber' | 'tableName'>): string {
  const calling = tt('Kuchyňa volá')
  const table = call.tableName === null ? null : `${tt('Stôl')} ${call.tableName}`
  const number = call.orderNumber === null ? null : `#${call.orderNumber}`
  if (table !== null && number !== null) return `${calling}: ${table} (${number})`
  if (table !== null) return `${calling}: ${table}`
  if (number !== null) return `${calling}: ${number}`
  return calling
}

/** Only what the rule reads of a channel, so any channel-shaped list can be passed. */
export interface CallableChannel {
  slug: string
  onPremise: boolean
}

/**
 * Whether a kitchen ticket offers the bell - an **on-premise** order only, §9.720.
 *
 * <p>Asked of the channel's `onPremise`, never of its name: a franchise's *Terasa* is a room with waiters exactly as
 * *Reštaurácia* is. A delivery has none - its driver is not called this way.
 *
 * <p><b>An unresolved channel falls back to `restaurant`</b>, the same fallback the board's own ordering
 * (`kitchenTicketOrder`) uses while the channels have not answered, so the two cannot disagree about which tickets are
 * in the room.
 */
export function kitchenCallOffered(type: string | null | undefined, channels: readonly CallableChannel[]): boolean {
  const slug = type ?? ''
  const known = channels.find((channel) => channel.slug === slug)
  if (known !== undefined) return known.onPremise
  return slug === 'restaurant'
}
